if (!customElements.get('annur-collection-product-carousel')) {
  class AnnurCollectionProductCarousel extends HTMLElement {
    connectedCallback() {
      this.stopAutoplay();
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.slides = [...this.querySelectorAll('[data-annur-collection-slide]')];
      this.tabs = [...this.querySelectorAll('[data-annur-collection-tab]')];
      this.previousButton = this.querySelector('[data-annur-collection-previous]');
      this.nextButton = this.querySelector('[data-annur-collection-next]');
      this.pauseButton = this.querySelector('[data-annur-collection-pause]');
      this.controls = this.querySelector('[data-annur-collection-controls]');
      this.status = this.querySelector('[data-annur-collection-status]');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      const configuredSeconds = Number.parseInt(this.dataset.changeSeconds, 10);
      this.changeInterval = Math.min(10, Math.max(3, configuredSeconds || 5)) * 1000;
      this.currentIndex = 0;
      this.userPaused = false;
      this.interactionPaused = false;
      this.assistivePaused = document.body.classList.contains('a11y-reduce-motion');

      if (!this.slides.length) return;
      this.dataset.ready = 'true';
      if (this.slides.length > 1) this.controls?.removeAttribute('hidden');
      this.show(0, false);
      this.updatePauseButton();

      this.previousButton?.addEventListener('click', () => this.move(-1), { signal });
      this.nextButton?.addEventListener('click', () => this.move(1), { signal });
      this.pauseButton?.addEventListener('click', () => this.toggleAutoplay(), { signal });
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index), { signal });
        tab.addEventListener('keydown', (event) => this.handleTabKeydown(event, index), { signal });
      });
      this.addEventListener('mouseenter', () => this.setInteractionPaused(true), { signal });
      this.addEventListener('mouseleave', () => this.setInteractionPaused(false), { signal });
      this.addEventListener('focusin', () => this.setInteractionPaused(true), { signal });
      this.addEventListener(
        'focusout',
        (event) => {
          if (!this.contains(event.relatedTarget)) this.setInteractionPaused(false);
        },
        { signal },
      );
      this.motionQuery.addEventListener(
        'change',
        () => {
          this.updatePauseButton();
          this.restartAutoplay();
        },
        { signal },
      );
      document.addEventListener('visibilitychange', () => this.restartAutoplay(), { signal });
      window.addEventListener(
        'annur:reduce-motion-change',
        (event) => {
          this.assistivePaused = event.detail.active;
          this.updatePauseButton();
          this.restartAutoplay();
        },
        { signal },
      );
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.abortController?.abort();
    }

    show(index, announce) {
      this.currentIndex = index;
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.dataset.active = String(active);
        slide.toggleAttribute('inert', !active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      if (announce && this.status) {
        this.status.textContent = `Producto ${index + 1} de ${this.slides.length}: ${this.slides[index].dataset.title}`;
      }
    }

    select(index) {
      this.show(index, true);
      this.userPaused = true;
      this.updatePauseButton();
      this.restartAutoplay();
    }

    move(direction) {
      this.select((this.currentIndex + direction + this.slides.length) % this.slides.length);
    }

    handleTabKeydown(event, index) {
      const direction = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
      let targetIndex = index;
      if (direction) targetIndex = (index + direction + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') targetIndex = 0;
      if (event.key === 'End') targetIndex = this.tabs.length - 1;
      if (targetIndex === index && !direction && event.key !== 'Home' && event.key !== 'End') return;
      event.preventDefault();
      this.select(targetIndex);
      this.tabs[targetIndex]?.focus();
    }

    toggleAutoplay() {
      this.userPaused = !this.userPaused;
      this.updatePauseButton();
      this.restartAutoplay();
    }

    updatePauseButton() {
      if (!this.pauseButton) return;
      this.dataset.userPaused = String(this.userPaused);
      const unavailable =
        !this.autoplayEnabled || this.slides.length <= 1 || this.motionQuery.matches || this.assistivePaused;
      this.pauseButton.toggleAttribute('hidden', unavailable);
      this.pauseButton.textContent = this.userPaused ? 'Reanudar' : 'Pausar';
      this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
      this.pauseButton.setAttribute(
        'aria-label',
        this.userPaused ? 'Reanudar rotacion automatica' : 'Pausar rotacion automatica',
      );
    }

    setInteractionPaused(paused) {
      this.interactionPaused = paused;
      this.dataset.interactionPaused = String(paused);
      this.restartAutoplay();
    }

    startAutoplay() {
      if (
        !this.autoplayEnabled ||
        this.slides.length <= 1 ||
        this.userPaused ||
        this.interactionPaused ||
        this.assistivePaused ||
        this.motionQuery.matches ||
        document.hidden
      )
        return;
      this.autoplayTimer = window.setInterval(() => {
        this.show((this.currentIndex + 1) % this.slides.length, false);
      }, this.changeInterval);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = undefined;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  customElements.define('annur-collection-product-carousel', AnnurCollectionProductCarousel);
}
